import {isNumber} from 'lodash';
import {DateTime, Duration} from 'luxon';

import {LocalizeRenderer} from '../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {newPalette, NewPaletteColorsEnum} from '../../../libs/shared/ui/src/styles/colorStyles';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsTableMetric,
  isBucketDataPoint,
} from '../../models/api/analyticsMetricsModel';
import {ChartSerie} from '../components/charts/commons/types';
import {TableToGraphTransformer} from './analyticsMetricsTransformers';
import {
  AnalyticsComputedGraphMetricBase,
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedMetricTypesEnum,
} from './analyticsMetricsTransformersTypes';

// Copied from the backend.
enum IntervalsEnum {
  HOURS = '1h',
  DAYS = '1d',
  WEEKS = '1w',
  MONTHS = '1M',
}

function getInterval(duration: Duration) {
  // This is made to match the fact that for a set filter of 3 days, we will have only 3 data points
  // at exactly 12am, which is a duration of exactly 48 hours. And we want it to be displayed as
  // days rather than hours. A 2 days and less set filter has a duraction of 47 hours and has one
  // data point per hour.
  if (Math.round(duration.as('hours')) < 48) {
    return IntervalsEnum.HOURS;
  }

  if (Math.round(duration.as('days')) <= 31) {
    return IntervalsEnum.DAYS;
  }

  if (Math.round(duration.as('weeks')) < 12) {
    return IntervalsEnum.WEEKS;
  }

  return IntervalsEnum.MONTHS;
}

// Copied from the backend.
function makeLabel(start: DateTime, end: DateTime, interval: IntervalsEnum, count: number) {
  switch (interval) {
    case IntervalsEnum.HOURS:
      if (count > 24) {
        // Wed 15, 6 AM
        return start.toFormat('ccc dd, h a'); // When dealing with data over 24h, prefix label with weekday.
      }
      // 1 AM
      return start.toFormat('h a');
    case IntervalsEnum.DAYS:
      if (count <= 7) {
        // Wed, Jun 5
        return start.toFormat('ccc, LLL d');
      }

      // Jun 5
      return start.toFormat('LLL d');
    case IntervalsEnum.WEEKS:
      // Jun 5 - Jun 12
      return `${start.toFormat('LLL d')} - ${end.toFormat('LLL d')}`;
    case IntervalsEnum.MONTHS:
      // Jun 10
      return start.toFormat('LLL yyyy');
    default:
      throw new Error(`Invalid interval: ${interval}`);
  }
}

/*
 * Type.
 */

export enum WhatHappenedSerieTypesEnum {
  NEW_SEGMENTS = 'new_segments',
  CLOSED_SEGMENTS = 'closed_segments',
  REMAINING_OPEN = 'remaining_open',
}

export interface WhatHappenedChartSeries {
  label: string;
  type: AnalyticsDataPointTypesEnum;
  bucketKey: number;
  [WhatHappenedSerieTypesEnum.NEW_SEGMENTS]: number;
  [WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS]: number;
  [WhatHappenedSerieTypesEnum.REMAINING_OPEN]: number;
}

interface WhatHappenedGraphMetric extends AnalyticsComputedGraphMetricBase<WhatHappenedChartSeries> {}

/*
 * Const.
 */

// Label for each submetric.
const whatHappenedSubmetricsLabels: Record<WhatHappenedSerieTypesEnum, LocalizeRenderer> = {
  [WhatHappenedSerieTypesEnum.NEW_SEGMENTS]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-new-workload',
        message: 'New workload',
      }),
    ),
  [WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-closed-workload',
        message: 'Closed workload',
      }),
    ),
  [WhatHappenedSerieTypesEnum.REMAINING_OPEN]: (localize) =>
    localize(
      defineMessage({
        id: 'analytics-remaining-open',
        message: 'Remaining open',
      }),
    ),
};

export const getWhatHappenedSeries: (localize: Localizer) => Array<ChartSerie<WhatHappenedChartSeries>> = (
  localize: Localizer,
) => [
  {
    accessor: (d) => d?.[WhatHappenedSerieTypesEnum.NEW_SEGMENTS],
    label: getWhatHappenedSubmetricLabel(localize, WhatHappenedSerieTypesEnum.NEW_SEGMENTS),
    color: newPalette[NewPaletteColorsEnum.GREEN].regular,
    key: WhatHappenedSerieTypesEnum.NEW_SEGMENTS,
  },
  {
    accessor: (d) => d?.[WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS],
    label: getWhatHappenedSubmetricLabel(localize, WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS),
    color: newPalette[NewPaletteColorsEnum.GREY].regular,
    key: WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS,
  },
  {
    accessor: (d) => d?.[WhatHappenedSerieTypesEnum.REMAINING_OPEN],
    label: getWhatHappenedSubmetricLabel(localize, WhatHappenedSerieTypesEnum.REMAINING_OPEN),
    color: newPalette[NewPaletteColorsEnum.BLUE].lowlight,
    key: WhatHappenedSerieTypesEnum.REMAINING_OPEN,
  },
];

/*
 * Transformer.
 */

export const whatHappenedTableToGraph: TableToGraphTransformer = (
  metricData: AnalyticsTableMetric | undefined,
): WhatHappenedGraphMetric => {
  const transformedMetricBase = {
    id: AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH,
    metricType: AnalyticsComputedMetricTypesEnum.CUSTOM_GRAPH,
    isEmpty: metricData?.isEmpty ?? false,
  };

  if (!metricData) {
    return {
      ...transformedMetricBase,
      values: [],
    };
  }

  // Get first two buckets to find the bucket duration.
  const firstRow = metricData.rows[0];
  const secondRow = metricData.rows[1];
  const lastRow = metricData.rows[metricData.rows.length - 1];
  const firstTimestamp = firstRow && isBucketDataPoint(firstRow[0]) && firstRow[0].key;
  const secondTimestamp = secondRow && isBucketDataPoint(secondRow[0]) && secondRow[0].key;
  const lastTimestamp = lastRow && isBucketDataPoint(lastRow[0]) && lastRow[0].key;
  if (secondTimestamp === false || firstTimestamp === false || lastTimestamp === false) {
    throw new Error("What happened table - Cells aren't buckets");
  } // Shouldn't happen but means data is corrupted.

  const duration = DateTime.fromMillis(secondTimestamp).diff(DateTime.fromMillis(firstTimestamp));
  const interval = getInterval(DateTime.fromMillis(lastTimestamp).diff(DateTime.fromMillis(firstTimestamp)));

  return {
    ...transformedMetricBase,
    values: metricData.rows.map((row) => {
      // First row is the bucket one.
      const timestamp = isBucketDataPoint(row[0]) && row[0].key;

      if (!isNumber(timestamp)) {
        throw new Error("What happened table - Cell isn't a bucket");
      } // Shouldn't happen but means data is corrupted.

      const start = DateTime.fromMillis(timestamp);
      const end = start.plus(duration);

      return {
        label: makeLabel(start, end, interval, metricData.rows.length),
        type: AnalyticsDataPointTypesEnum.NUMERICAL,
        bucketKey: timestamp,
        ...[
          WhatHappenedSerieTypesEnum.NEW_SEGMENTS,
          WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS,
          WhatHappenedSerieTypesEnum.REMAINING_OPEN,
        ].reduce(
          (acc, key, i) => ({
            ...acc,
            [key]: row[i + 1].value,
          }),
          {
            [WhatHappenedSerieTypesEnum.NEW_SEGMENTS]: 0,
            [WhatHappenedSerieTypesEnum.CLOSED_SEGMENTS]: 0,
            [WhatHappenedSerieTypesEnum.REMAINING_OPEN]: 0,
          },
        ),
      };
    }),
  };
};

/*
 * Helpers.
 */

export function getWhatHappenedSubmetricLabel(
  localize: Localizer,
  submetric?: WhatHappenedSerieTypesEnum,
): string {
  if (!submetric) {
    return '';
  }

  return whatHappenedSubmetricsLabels[submetric]?.(localize) || submetric;
}
